import React, { useState, useEffect } from 'react';
import {
	Modal,
	Form,
	Input,
	Select,
	Upload,
	message,
	Button,
	Progress,
} from 'antd';
import { InboxOutlined, UploadOutlined } from '@ant-design/icons';
import { s3Api } from '../../api/s3';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import './FileUploadModal.css';
import { useAuth } from '../../context/AuthContext';

const { Option } = Select;
const { Dragger } = Upload;

function FileUploadModal({
	visible,
	onClose,
	onSubmit,
	folders = [],
	tags = [],
	currentFolderId = null,
	organizationId,
	// loading = false,
	preSelectedFiles = null,
	hideLocationSelect = false,
	defaultFolderId = null,
}) {
	const [form] = Form.useForm();
	const [fileList, setFileList] = useState([]);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [imageLoaded, setImageLoaded] = useState(false);
	const [thumbnailFile, setThumbnailFile] = useState(null);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isUploading, setIsUploading] = useState(false);
	const [loading, setLoading] = useState(false);
	const { checkStorageLimit } = useAuth();

	const defaultLayoutPluginInstance = defaultLayoutPlugin();

	// Update fileList when preSelectedFiles changes
	useEffect(() => {
		if (preSelectedFiles) {
			const files = Array.isArray(preSelectedFiles)
				? preSelectedFiles
				: [preSelectedFiles];
			setFileList(files);

			// Set initial form values
			form.setFieldsValue({
				folderId: defaultFolderId || currentFolderId || 'root',
				tagId: undefined,
			});
		}
	}, [preSelectedFiles, currentFolderId, defaultFolderId, form]);

	// Reset preview when file changes
	useEffect(() => {
		if (fileList.length > 0) {
			const file = fileList[0];
			const url = URL.createObjectURL(file);
			setPreviewUrl(url);
			setImageLoaded(false);
			return () => URL.revokeObjectURL(url);
		} else {
			setPreviewUrl(null);
			setImageLoaded(false);
		}
	}, [fileList]);

	const getFileType = (file) => {
		// If we have a MIME type, use it
		if (file.type) {
			return file.type;
		}

		// If not, infer from extension
		const extension = file.name.split('.').pop().toLowerCase();
		const mimeTypes = {
			jpg: 'image/jpeg',
			jpeg: 'image/jpeg',
			png: 'image/png',
			gif: 'image/gif',
			pdf: 'application/pdf',
			mp4: 'video/mp4',
			mp3: 'audio/mpeg',
			doc: 'application/msword',
			docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
			xls: 'application/vnd.ms-excel',
			xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			ppt: 'application/vnd.ms-powerpoint',
			pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
			txt: 'text/plain',
			csv: 'text/csv',
			// Add more as needed
		};

		return mimeTypes[extension] || `application/${extension}`;
	};

	// Helper function to check if file is an image
	const isImageFile = (file) => {
		const fileType = file.type || getFileType(file);
		return (
			fileType.startsWith('image/') ||
			['jpg', 'jpeg', 'png', 'gif'].includes(
				file.name.split('.').pop().toLowerCase()
			)
		);
	};

	// Helper function to get display-friendly file type
	const getDisplayFileType = (file) => {
		const type = file.type || getFileType(file);
		if (type.startsWith('image/')) return type;

		const extension = file.name.split('.').pop().toLowerCase();
		switch (extension) {
			case 'jpg':
			case 'jpeg':
				return 'image/jpeg';
			case 'png':
				return 'image/png';
			case 'gif':
				return 'image/gif';
			case 'pdf':
				return 'application/pdf';
			default:
				return type || 'Unknown';
		}
	};

	// Update the handleThumbnailUpload function to properly handle S3 upload
	const handleThumbnailUpload = async (file) => {
		try {
			const timestamp = Date.now();
			const fileExtension = file.name.split('.').pop().toLowerCase();
			// Fix case sensitivity - ensure "Images" is capitalized
			const thumbnailFileName = `Images/thumbnails/thumbnail-${timestamp}.${fileExtension}`;

			// Upload thumbnail to S3 using s3Api without formatting the Images folder name
			const thumbnailUrl = await s3Api.upload(
				organizationId,
				file,
				thumbnailFileName,
				null,
				(progress) => {
					setUploadProgress(progress * 0.2);
				},
				true // Add a flag to prevent lowercase conversion for this path
			);

			return thumbnailUrl;
		} catch (error) {
			console.error('Thumbnail upload error:', error);
			message.error('Failed to upload thumbnail');
			throw error;
		}
	};

	// Update handleSubmit to include progress handling
	const handleSubmit = async (values) => {
		try {
			setLoading(true);

			// Calculate total size of all files
			const totalSize = fileList.reduce(
				(sum, file) => sum + file.size,
				0
			);

			// Check storage limit before uploading
			const storageCheck = await checkStorageLimit(totalSize);
			if (!storageCheck.canUpload) {
				message.error(storageCheck.message);
				setLoading(false);
				return;
			}

			if (fileList.length === 0) {
				message.error('Please select files to upload');
				return;
			}

			setIsUploading(true);
			setUploadProgress(0);

			// Handle single file
			if (fileList.length === 1) {
				const file = fileList[0];
				const targetFolderId =
					defaultFolderId || currentFolderId || 'root';
				const filePath = `${targetFolderId}/${file.name}`;

				// Upload thumbnail if exists
				let thumbnailUrl = null;
				if (thumbnailFile) {
					const thumbnailFileName = `thumbnails/${Date.now()}_${
						thumbnailFile.name
					}`;
					thumbnailUrl = await s3Api.upload(
						organizationId,
						thumbnailFile,
						thumbnailFileName
					);
					setUploadProgress(20);
				}

				// Create FormData for API
				const formData = new FormData();
				formData.append('name', values.name || file.name);
				formData.append(
					'folderId',
					targetFolderId === 'root' ? null : targetFolderId
				);
				formData.append('tagId', values.tagId || null);
				formData.append('thumbnailUrl', thumbnailUrl);
				formData.append('filePath', filePath);

				await onSubmit({
					file,
					formData,
					onProgress: (progress) => {
						const baseProgress = thumbnailFile ? 20 : 0;
						const fileProgress =
							progress * (thumbnailFile ? 0.8 : 1);
						setUploadProgress(baseProgress + fileProgress);
					},
				});

				message.success('File uploaded successfully');
			} else {
				// Handle multiple files
				let completedFiles = 0;
				const totalFiles = fileList.length;
				const targetFolderId =
					defaultFolderId || currentFolderId || 'root';

				const uploadPromises = fileList.map((file) => {
					const filePath = `${targetFolderId}/${file.name}`;
					const formData = new FormData();
					formData.append('name', file.name);
					formData.append('folderId', targetFolderId);
					formData.append('tagId', values.tagId);
					formData.append('filePath', filePath);

					return onSubmit({
						file,
						formData,
						onProgress: (progress) => {
							const fileProgress = progress / 100 / totalFiles;
							const previousFilesProgress =
								(completedFiles / totalFiles) * 100;
							setUploadProgress(
								previousFilesProgress + fileProgress * 100
							);
						},
						onComplete: () => {
							completedFiles += 1;
						},
					});
				});

				await Promise.all(uploadPromises);
				message.success(
					`Successfully uploaded ${fileList.length} files`
				);
			}

			setUploadProgress(100);
			setTimeout(() => {
				setIsUploading(false);
				setUploadProgress(0);
				onClose();
			}, 500);
		} catch (error) {
			console.error('Upload error:', error);
			message.error('Failed to upload: ' + error.message);
			setIsUploading(false);
			setUploadProgress(0);
		} finally {
			setLoading(false);
		}
	};

	const uploadProps = {
		beforeUpload: (file) => {
			setFileList([file]);
			return false;
		},
		fileList,
		multiple: false,
		onRemove: () => {
			setFileList([]);
			setPreviewUrl(null);
		},
	};

	// Add thumbnail upload props
	const thumbnailUploadProps = {
		beforeUpload: (file) => {
			if (!file.type.startsWith('image/')) {
				message.error('You can only upload image files as thumbnails!');
				return false;
			}
			setThumbnailFile(file);
			return false;
		},
		onRemove: () => {
			setThumbnailFile(null);
		},
		accept: 'image/*',
		maxCount: 1,
		fileList: thumbnailFile ? [thumbnailFile] : [],
	};

	const renderPreview = () => {
		if (!fileList.length) return null;

		const file = fileList[0];
		const fileType = file.type || getFileType(file);

		const isImage =
			fileType.startsWith('image/') ||
			['jpg', 'jpeg', 'png', 'gif'].includes(
				file.name.split('.').pop().toLowerCase()
			);

		if (isImage) {
			return (
				<div className='preview-section'>
					{!imageLoaded && (
						<div className='media-preview-ghost'>
							<div className='ghost-gradient'></div>
						</div>
					)}
					<img
						src={previewUrl}
						alt={file.name}
						className={`media-preview-image ${
							imageLoaded ? 'loaded' : ''
						}`}
						onLoad={() => setImageLoaded(true)}
					/>
				</div>
			);
		} else if (fileType.startsWith('video/')) {
			return (
				<video
					controls
					className='media-preview-video'>
					<source
						src={previewUrl}
						type={fileType}
					/>
					Your browser does not support the video tag.
				</video>
			);
		} else if (fileType.startsWith('audio/')) {
			return (
				<audio
					controls
					className='media-preview-audio'>
					<source
						src={previewUrl}
						type={fileType}
					/>
					Your browser does not support the audio tag.
				</audio>
			);
		} else if (
			fileType === 'application/pdf' ||
			file.name.toLowerCase().endsWith('.pdf')
		) {
			return (
				<div className='media-preview-pdf'>
					<Worker workerUrl='https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'>
						<Viewer
							fileUrl={previewUrl}
							plugins={[defaultLayoutPluginInstance]}
							defaultScale={1}
						/>
					</Worker>
				</div>
			);
		} else {
			return (
				<div className='media-preview-default'>
					<span className='material-icons'>description</span>
					<p>{file.name}</p>
					<p className='file-type'>Type: {fileType}</p>
				</div>
			);
		}
	};

	const isMultipleFiles = fileList.length > 1;
	const isSingleDraggedFile = preSelectedFiles && !isMultipleFiles;

	// Update the form section to include the progress bar
	const renderForm = () => (
		<div className='form-section'>
			{isUploading && (
				<div style={{ marginBottom: 16 }}>
					<Progress
						percent={Math.round(uploadProgress)}
						status={uploadProgress === 100 ? 'success' : 'active'}
						strokeColor={{
							'0%': '#108ee9',
							'100%': '#87d068',
						}}
					/>
				</div>
			)}
			<Form
				form={form}
				layout='vertical'
				onFinish={handleSubmit}>
				{/* Show name field for single files (both dragged and uploaded) */}
				{!isMultipleFiles && (
					<Form.Item
						name='name'
						label='Name'
						initialValue={fileList[0]?.name}
						rules={[
							{
								required: true,
								message: 'Please enter a name',
							},
						]}>
						<Input />
					</Form.Item>
				)}

				{/* Show folder selection for single files or when not dragged */}
				{(!isMultipleFiles || !preSelectedFiles) && (
					<Form.Item
						name='folderId'
						label='Location'
						initialValue={currentFolderId || 'root'}>
						<Select>
							<Option value='root'>Root</Option>
							{folders.map((folder) => (
								<Option
									key={folder.id}
									value={folder.id}>
									{folder.name}
								</Option>
							))}
						</Select>
					</Form.Item>
				)}

				<Form.Item
					name='tagId'
					label={
						isMultipleFiles
							? 'Tag (will be applied to all files)'
							: 'Tag'
					}>
					<Select allowClear>
						{tags.map((tag) => (
							<Option
								key={tag.id}
								value={tag.id}>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}>
									<div
										style={{
											width: 16,
											height: 16,
											backgroundColor: tag.color,
											borderRadius: 4,
										}}
									/>
									{tag.name}
								</div>
							</Option>
						))}
					</Select>
				</Form.Item>

				{/* Add thumbnail upload option for audio/video files */}
				{!isMultipleFiles &&
					fileList[0] &&
					(fileList[0].type?.startsWith('video/') ||
						fileList[0].type?.startsWith('audio/')) && (
						<Form.Item
							label='Thumbnail (optional)'
							tooltip='Upload a thumbnail image for this media file'>
							<Upload {...thumbnailUploadProps}>
								<Button icon={<UploadOutlined />}>
									Upload Thumbnail
								</Button>
							</Upload>
						</Form.Item>
					)}

				<Form.Item>
					<div style={{ display: 'flex', gap: '8px' }}>
						<Button
							onClick={onClose}
							style={{ flex: 1 }}
							disabled={isUploading}>
							Cancel
						</Button>
						<Button
							type='primary'
							htmlType='submit'
							loading={loading || isUploading}
							style={{ flex: 1 }}>
							{isUploading
								? `Uploading ${Math.round(uploadProgress)}%`
								: 'Upload'}
						</Button>
					</div>
				</Form.Item>
			</Form>
		</div>
	);

	return (
		<Modal
			title={
				isMultipleFiles
					? `Upload ${fileList.length} Files`
					: 'Upload Media'
			}
			open={visible}
			onCancel={onClose}
			footer={null}
			className='file-upload-modal common-modal'
			width={800}>
			{!preSelectedFiles && (
				<div className='upload-section'>
					<Dragger {...uploadProps}>
						<p className='ant-upload-drag-icon'>
							<InboxOutlined />
						</p>
						<p className='ant-upload-text'>
							Click or drag files to upload
						</p>
						<p className='ant-upload-hint'>
							Support for single or bulk upload. Strictly
							prohibited from uploading company data or other
							banned files.
						</p>
					</Dragger>
				</div>
			)}

			{previewUrl && (
				<div className='preview-container'>{renderPreview()}</div>
			)}

			{renderForm()}
		</Modal>
	);
}

export default FileUploadModal;
